import {Component, inject, OnInit, signal} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {NgbModal, NgbPagination} from "@ng-bootstrap/ng-bootstrap";
import {Page, UserManageGroupDto, UserManageGroupSearchParams} from '../../../generated-model/model';
import {DecimalPipe, NgForOf, NgIf} from '@angular/common';
import {NotFoundComponent} from '../../shared/not-found/not-found.component';
import {ManagerUserModalComponent} from './manager-user-modal/manager-user-modal.component';
import {UserRest} from '../../service/UserRest';
import {IgnoreNullHttpParams} from '../../shared/Ignore-null-http-params';
import {from} from 'rxjs';
import {filter} from 'rxjs/operators';
import {HasAnyRoleDirective} from '../../auth/has-any-role.directive';
import {ManagerUserModalEditComponent} from './manager-user-modal-edit/manager-user-modal-edit.component';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {ResourceAccess, SsoUser} from '../../app.constants';
import {ToastService} from '../../shared/commons/toast/toast-service';
import {RoleService} from '../../shared/commons/services/role.service';
import {UserService} from '../../shared/commons/services/user.service';
import {ConfirmDialogService} from '../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {ManufacturerRest} from '../../service/ManufacturerRest';

@Component({
  selector: 'app-manager-user',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgbPagination,
    NgForOf,
    NgIf,
    NotFoundComponent,
    DecimalPipe,
    FormsModule,
    HasAnyRoleDirective
  ],
  templateUrl: './manager-user.component.html',
  styleUrl: './manager-user.component.scss'
})
export class ManagerUserComponent implements OnInit {
  pageData = signal<Page<UserManageGroupDto>>({size: 10})
  searchParams = signal<UserManageGroupSearchParams>({enable: true})
  userData = signal<SsoUser>(undefined)
  userService = inject(UserRest)
  private modalService = inject(NgbModal);
  roleService = inject(RoleService)
  toastService = inject(ToastService)
  hasAdminRole = false;
  userDataService = inject(UserService)
  confirmDialogService = inject(ConfirmDialogService)
  manufacturerRest = inject(ManufacturerRest)

  ngOnInit() {
    this.search();
    this.userDataService.getUser().subscribe({
      next: (userData: SsoUser | null) => {
        this.userData.set(userData);
        console.log(this.userData())
      }
    })

    this.roleService.hasRole('user_admin').subscribe({
      next: e => {
        this.hasAdminRole = e
      }
    })
  }

  search(page: number = 1) {
    const pageRequest = {page: page - 1, size: this.pageData().size, sort: 'createdDate,desc'}
    const params = IgnoreNullHttpParams.fromObject({...this.searchParams()})
      .set("page", pageRequest.page)
      .set("size", pageRequest.size)
      .set("sort", pageRequest.sort)
    this.userService.searchGroup(params).subscribe({
      next: value => {
        this.pageData.set(value)
      },
      error() {
        console.log("error")
      }
    })
  }

  clear() {
    this.searchParams.set({enable: true})
    this.search()
  }

  openModel(prefix: string) {
    if (prefix == 'เพิ่ม') {
      const modalRef = this.modalService.open(ManagerUserModalComponent, {
        size: 'xl'
      });
      if (this.manufacturerRest?.manufacturer?.businessType && this.manufacturerRest?.manufacturer?.businessType !== 'FARMER') {
        modalRef.componentInstance.prefix = 'ระบุ e-Mail เพื่อเพิ่มผู้ใช้งานในกลุ่ม/บริษัท'
      } else {
        modalRef.componentInstance.prefix = 'ระบุ e-Mail เพื่อเพิ่มผู้ใช้งาน'
      }
      from(modalRef.result).pipe(filter(e => !!e))
        .subscribe(e => {
          this.search(1)
        });
    }
  }

  openModelEdit(item: UserManageGroupDto) {
    const modalRef = this.modalService.open(ManagerUserModalEditComponent, {
      size: 'xl'
    });
    if (this.manufacturerRest?.manufacturer?.businessType && this.manufacturerRest?.manufacturer?.businessType !== 'FARMER') {
      modalRef.componentInstance.prefix = 'ข้อมูลผู้ใช้งานในกลุ่ม/บริษัท'
    } else {
      modalRef.componentInstance.prefix = 'ข้อมูลผู้ใช้งาน'
    }
    modalRef.componentInstance.userManageGroupDto = {...item}
    from(modalRef.result).pipe(filter(e => !!e))
      .subscribe(e => {
        this.search(1)
      });
  }

  onStatusChange(item: UserManageGroupDto) {
    if (item.id) {
      // สร้างข้อความสำหรับการยืนยัน
      const newStatus = !item.enable;
      const confirmMessage = `ยืนยันการเปลี่ยนสถานะเป็น ${newStatus ? "เปิดใช้งาน" : "ปิดใช้งาน"}`;

      // สร้าง clone ของ item เพื่อไม่ให้กระทบกับค่าเดิม
      const updatedItem = {...item, enable: newStatus};

      this.confirmDialogService.open({content: confirmMessage})
        .pipe(filter(result => !!result))
        .subscribe({
          next: () => {
            this.userService.enableOrDisableUser(updatedItem).subscribe({
              next: () => {
                // อัพเดต UI เมื่อสำเร็จ
                item.enable = newStatus;
                this.toastService.showSuccess("เปลี่ยนสถานะสำเร็จ");
              },
              error: (err) => {
                const errorMessage = err.error?.message || "มีข้อผิดพลาด";
                this.toastService.showDangerWithMessage(errorMessage);
                // ไม่ต้องทำอะไรกับ UI เพราะเราใช้ [checked] ซึ่งยังคงค่าเดิมอยู่
              }
            });
          }
          // ไม่จำเป็นต้องมี error handler ที่นี่เพราะเราใช้ [checked] ซึ่งจะไม่เปลี่ยนค่า UI
        });
    }
  }

  onRoleChange(item: UserManageGroupDto) {
    console.log("Confirming role change for:", item);

    if (item.id) {
      // สร้างข้อความสำหรับการยืนยัน
      const newAdminStatus = !item.admin;
      const confirmMessage = `ยืนยันการเปลี่ยนบทบาทเป็น ${newAdminStatus ? "ADMIN" : "USER"}`;

      // สร้าง clone ของ item เพื่อไม่ให้กระทบกับค่าเดิม
      const updatedItem = {...item, admin: newAdminStatus};

      this.confirmDialogService.open({content: confirmMessage})
        .pipe(filter(result => !!result))
        .subscribe({
          next: () => {
            this.userService.changeUserRole(updatedItem).subscribe({
              next: () => {
                // อัพเดต UI เมื่อสำเร็จ
                item.admin = newAdminStatus;
                this.toastService.showSuccess("เปลี่ยนบทบาทสำเร็จ");
              },
              error: (err) => {
                const errorMessage = err.error?.message || "มีข้อผิดพลาด";
                this.toastService.showDangerWithMessage(errorMessage);
                // ไม่ต้องทำอะไรกับ UI เพราะเราใช้ [checked] ซึ่งยังคงค่าเดิมอยู่
              }
            });
          }
          // ไม่จำเป็นต้องมี error handler ที่นี่เพราะเราใช้ [checked] ซึ่งจะไม่เปลี่ยนค่า UI
        });
    }
  }

  private checkAdminRole(payload: any): boolean {
    if (payload) {
      const resourceAccess = payload as ResourceAccess;
      if (resourceAccess?.resource_access) {
        const qrtracesAccess = resourceAccess?.resource_access?.['qrtraces'];
        if (qrtracesAccess && qrtracesAccess.roles) {
          return qrtracesAccess.roles.includes('user_admin');
        }
      }
    }
    return false;
  }
}
